import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from "react";
import { getHabits, toggleHabit, createHabit } from "./api/habits.js";
import { getFood, addFood, deleteFood, updateFood, getTargets } from "./api/nutrition.js";
import { getWater, setWater } from "./api/water.js";
import { getNowPlaying, playSpotify, pauseSpotify, nextTrack, previousTrack } from "./api/spotify.js";
import Background from "./components/Background.js";
import TopBar from "./components/TopBar.js";
import Clock from "./components/Clock.js";
import HabitList from "./components/HabitList.js";
import SpotifyBar from "./components/SpotifyBar.js";
import QuickStats from "./components/QuickStats.js";
import PlusButton from "./components/PlusButton.js";
import Drawer from "./components/Drawer/Drawer.js";
import s from "./App.module.css";
const today = () => new Date().toISOString().slice(0, 10);
export default function App() {
    const [habits, setHabits] = useState([]);
    const [food, setFood] = useState(null);
    const [water, setWaterState] = useState(null);
    const [targets, setTargets] = useState(null);
    const [nowPlaying, setNowPlaying] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerTab, setDrawerTab] = useState("habits");
    const [photoCategory, setPhotoCategory] = useState(() => localStorage.getItem("diurne_photo_cat") ?? "tokyo");
    const [refreshSeed, setRefreshSeed] = useState(0);
    const plusBtnRef = useRef(null);
    // Load initial data
    useEffect(() => {
        const d = today();
        Promise.all([
            getHabits().then(setHabits).catch(console.error),
            getFood(d).then(setFood).catch(console.error),
            getWater(d).then(setWaterState).catch(console.error),
            getTargets().then(setTargets).catch(console.error),
            getNowPlaying().then(setNowPlaying).catch(console.error),
        ]);
    }, []);
    // Spotify polling — 10s interval + re-poll on tab focus
    useEffect(() => {
        const poll = () => getNowPlaying().then(setNowPlaying).catch(console.error);
        const onVisible = () => { if (!document.hidden)
            poll(); };
        const id = setInterval(() => { if (!document.hidden)
            poll(); }, 10_000);
        document.addEventListener("visibilitychange", onVisible);
        return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
    }, []);
    // When a track is known, schedule a refetch exactly when it should end
    useEffect(() => {
        if (!nowPlaying?.isPlaying || !nowPlaying.durationMs)
            return;
        const remaining = nowPlaying.durationMs - nowPlaying.progressMs;
        // small buffer so Spotify has switched tracks before we ask
        const id = setTimeout(() => {
            getNowPlaying().then(setNowPlaying).catch(console.error);
        }, remaining + 800);
        return () => clearTimeout(id);
    }, [nowPlaying?.track, nowPlaying?.isPlaying]);
    // Client-side progress ticker — advances progressMs every second while playing
    useEffect(() => {
        const id = setInterval(() => {
            setNowPlaying((prev) => {
                if (!prev?.isPlaying)
                    return prev;
                const next = Math.min(prev.progressMs + 1000, prev.durationMs);
                return { ...prev, progressMs: next };
            });
        }, 1000);
        return () => clearInterval(id);
    }, []);
    const handleToggleHabit = useCallback((id) => {
        // Optimistic update
        setHabits((prev) => prev.map((h) => h.id === id ? { ...h, completedToday: !h.completedToday } : h));
        toggleHabit(id)
            .then(({ completedToday, streak }) => {
            setHabits((prev) => prev.map((h) => (h.id === id ? { ...h, completedToday, streak } : h)));
        })
            .catch(console.error);
    }, []);
    const handleAddFood = useCallback((body) => {
        // Optimistic: temp entry with negative id
        const tempId = -Date.now();
        const tempEntry = { id: tempId, name: body.name, kcal: body.kcal, proteinG: body.proteinG, loggedAt: new Date().toISOString() };
        setFood((prev) => {
            if (!prev)
                return { entries: [tempEntry], totalKcal: body.kcal, totalProteinG: body.proteinG };
            return {
                entries: [...prev.entries, tempEntry],
                totalKcal: prev.totalKcal + body.kcal,
                totalProteinG: prev.totalProteinG + body.proteinG,
            };
        });
        addFood(body)
            .then((real) => {
            setFood((prev) => {
                if (!prev)
                    return prev;
                return {
                    ...prev,
                    entries: prev.entries.map((e) => (e.id === tempId ? real : e)),
                };
            });
        })
            .catch(console.error);
    }, []);
    const handleDeleteFood = useCallback((id) => {
        setFood((prev) => {
            if (!prev)
                return prev;
            const entry = prev.entries.find((e) => e.id === id);
            if (!entry)
                return prev;
            return {
                entries: prev.entries.filter((e) => e.id !== id),
                totalKcal: prev.totalKcal - entry.kcal,
                totalProteinG: prev.totalProteinG - entry.proteinG,
            };
        });
        deleteFood(id).catch(console.error);
    }, []);
    const handleSetWater = useCallback((glasses) => {
        setWaterState((prev) => (prev ? { ...prev, glasses } : { glasses, date: today() }));
        setWater(glasses).catch(console.error);
    }, []);
    const handleCreateHabit = useCallback((name) => {
        createHabit({ name })
            .then((habit) => setHabits((prev) => [...prev, habit]))
            .catch(console.error);
    }, []);
    const handlePlay = useCallback(() => {
        setNowPlaying((prev) => prev ? { ...prev, isPlaying: true } : prev);
        playSpotify().catch(console.error);
    }, []);
    const handlePause = useCallback(() => {
        setNowPlaying((prev) => prev ? { ...prev, isPlaying: false } : prev);
        pauseSpotify().catch(console.error);
    }, []);
    const handleNext = useCallback(() => {
        nextTrack()
            .then(() => new Promise((r) => setTimeout(r, 700)))
            .then(() => getNowPlaying().then(setNowPlaying))
            .catch(console.error);
    }, []);
    const handlePrev = useCallback(() => {
        previousTrack()
            .then(() => new Promise((r) => setTimeout(r, 700)))
            .then(() => getNowPlaying().then(setNowPlaying))
            .catch(console.error);
    }, []);
    const handleUpdateFood = useCallback((id, body) => {
        setFood((prev) => {
            if (!prev)
                return prev;
            const entry = prev.entries.find((e) => e.id === id);
            if (!entry)
                return prev;
            const updated = { ...entry, ...body };
            const delta = { kcal: updated.kcal - entry.kcal, proteinG: updated.proteinG - entry.proteinG };
            return {
                entries: prev.entries.map((e) => (e.id === id ? updated : e)),
                totalKcal: prev.totalKcal + delta.kcal,
                totalProteinG: prev.totalProteinG + delta.proteinG,
            };
        });
        updateFood(id, body).catch(console.error);
    }, []);
    return (_jsxs(_Fragment, { children: [_jsx(Background, { category: photoCategory, refreshSeed: refreshSeed }), _jsxs("div", { className: s.ui, children: [_jsx(TopBar, { photoCategory: photoCategory, onPhotoCategory: (id) => { setPhotoCategory(id); localStorage.setItem("diurne_photo_cat", id); setRefreshSeed((n) => n + 1); }, onPhotoRefresh: () => setRefreshSeed((n) => n + 1) }), _jsx(Clock, {}), _jsx(HabitList, { habits: habits, onToggle: handleToggleHabit }), _jsxs("div", { className: s.bottom, children: [_jsx(SpotifyBar, { nowPlaying: nowPlaying, onPlay: handlePlay, onPause: handlePause, onNext: handleNext, onPrev: handlePrev }), _jsx(QuickStats, { water: water?.glasses ?? 0, waterTarget: 8, totalKcal: food?.totalKcal ?? 0, totalProteinG: food?.totalProteinG ?? 0, children: _jsx(PlusButton, { isOpen: drawerOpen, onClick: () => setDrawerOpen((o) => !o), ref: plusBtnRef }) })] })] }), _jsx(Drawer, { isOpen: drawerOpen, tab: drawerTab, onTabChange: setDrawerTab, onClose: () => setDrawerOpen(false), habits: habits, onToggleHabit: handleToggleHabit, onCreateHabit: handleCreateHabit, food: food, targets: targets, onAddFood: handleAddFood, onDeleteFood: handleDeleteFood, onUpdateFood: handleUpdateFood, water: water, onSetWater: handleSetWater, plusBtnRef: plusBtnRef })] }));
}
//# sourceMappingURL=App.js.map